/**
 * The translation catalogue and its runtime.
 *
 * One rule holds this together: a missing translation falls back to English
 * rather than showing a key or an empty string. A locale that is 60% translated
 * should be a usable app with some English in it, not a broken one -- otherwise
 * no locale can ship until it is perfect, and none ever is.
 */
import { directionOf, negotiateLocale } from '@vav/protocol';

import { en, type MessageKey } from './messages/en.ts';
import { he } from './messages/he.ts';
import { ar } from './messages/ar.ts';
import { es } from './messages/es.ts';
import { ptBR } from './messages/pt-BR.ts';
import { hi } from './messages/hi.ts';
import { ja } from './messages/ja.ts';

export { formatMessage, type MessageValues } from './icu.ts';
export { LOCALE_OPTIONS, type LocaleOption } from './locales.ts';
export {
  composeInternational,
  countryOptions,
  countryFor,
  countryNamesVersion,
  ensureCountryNames,
  matchesCountry,
  subscribeCountryNames,
  type CountryOption,
} from './countries.ts';
export type { MessageKey } from './messages/en.ts';

import { formatMessage, type MessageValues } from './icu.ts';

export type Catalogue = Partial<Record<MessageKey, string>>;

/**
 * Locales with a catalogue: the brief's full QA set.
 *
 * A locale appears here only when it has real translations. Stubbing a language
 * with English would make "translated" and "not translated yet" indistinguishable
 * from the outside, which is how a half-finished locale ships by accident.
 *
 * `pt-BR` is regional rather than plain `pt`: European and Brazilian Portuguese
 * differ enough in everyday vocabulary that one file serving both reads wrong
 * in at least one of them. Locale negotiation falls back `pt-BR` → `pt` → `en`,
 * so a `pt-PT` user still gets something usable.
 */
const CATALOGUES: Record<string, Catalogue> = { en, he, ar, es, 'pt-BR': ptBR, hi, ja };

export const AVAILABLE_LOCALES = Object.keys(CATALOGUES);

export interface Translator {
  /** The resolved locale, which may differ from what was requested. */
  locale: string;
  dir: 'ltr' | 'rtl';
  t: (key: MessageKey, values?: MessageValues) => string;
}

export function createTranslator(locale: string): Translator {
  const catalogue = CATALOGUES[locale] ?? CATALOGUES[locale.split('-')[0]] ?? {};

  return {
    locale,
    dir: directionOf(locale),
    t(key, values) {
      // English is the fallback for a key the translation has not reached yet.
      // The key itself is the last resort and means the key is misspelt.
      const pattern = catalogue[key] ?? en[key] ?? key;
      return formatMessage(locale, pattern, values);
    },
  };
}

/** Choose the best catalogue for the runtime's stated preferences. */
export function detectLocale(): string {
  return negotiateLocale(AVAILABLE_LOCALES, 'en');
}

// ---------------------------------------------------------------------------
// Ambient translator
// ---------------------------------------------------------------------------

/**
 * For code that cannot use a React hook.
 *
 * Some user-facing strings are produced outside the render tree: the text of a
 * thrown permission error, an OS notification-channel name, a push body built
 * in a background task. Those still have to be translated, and a hook is not
 * available to them.
 *
 * UI code must keep using the hook. This is a snapshot, so a component reading
 * it will not re-render when the locale changes -- which is exactly the bug it
 * looks like it solves.
 */
let ambient = createTranslator(detectLocale());

export function setAmbientLocale(locale: string): void {
  ambient = createTranslator(locale);
}

export function translate(key: MessageKey, values?: MessageValues): string {
  return ambient.t(key, values);
}
